<script setup>
import { computed, inject } from 'vue';
import ErrorDisplay from '../elements/ErrorDisplay.vue';

const props = defineProps({
    wordList: {
        type: Object,
    },
    subscriptionList: {
        type: Object,
        default: null,
    },
    errors: {
        type: Object
    }
});

const env = inject('env', {});

const configCategoryOptions = computed(() => {
    return env.configCategoryOptions ? JSON.parse(env.configCategoryOptions) : {};
});

const configCategoryLabels = computed(() => {
    return env.configCategoryLabels ? JSON.parse(env.configCategoryLabels) : {};
});

const configCategoryGroupLabels = computed(() => {
    return env.configCategoryGroupLabels ? JSON.parse(env.configCategoryGroupLabels) : {};
});

const configCategoryModel = computed({
    get: () => props.wordList.config_category ?? '',
    set: (value) => {
        props.wordList.config_category = value === '' ? null : value;
    },
});

</script>

<template>
    <input v-if="wordList.id" type="hidden" name="id" v-model="wordList.id" />
    <div class="input-group mb-3">
        <label>
            Titel
            <input class="form-control" type="text" name="title" v-model="wordList.title" />
        </label>
    </div>

    <div class="input-group mb-3">
        <label>
            Info-Text
            <textarea class="form-control" name="description" rows="5" v-model="wordList.description"></textarea>
        </label>
    </div>
    <div v-if="wordList.id == 0" class="input-group mb-3">
        <label>
            Listentyp
            <select class="form-control" name="relation_type" v-model="wordList.relation_type">
                <option value="0">einfache Liste</option>
                <option value="1">Liste - neutraler Thesaurus</option>
                <option value="2">Liste - Verbesserungsvorschläge (n Such-Ausdrücke - n Vorschläge)</option>
                <option value="3">Liste - Schreibweisen-Prüfung</option>
                <option value="4">Whitelist</option>
                <option value="5">allgemeine Regeln</option>
            </select>
        </label>
    </div>
    <div class="input-group mb-3">
        <label>
            Konfigurations-Kategorie
            <select class="form-control" name="config_category" v-model="configCategoryModel">
                <option value="">Keine Kategorie</option>
                <optgroup v-for="(values, group) in configCategoryOptions" :key="group"
                    :label="configCategoryGroupLabels[group] || group">
                    <option v-for="value in values" :key="value" :value="value">{{ configCategoryLabels[value] || value
                    }}</option>
                </optgroup>
            </select>
        </label>
    </div>
    <div class="form-check">
        <label class="form-check-label">
            <input type="hidden" name="active" value="0" />
            <input class="form-check-input" type="checkbox" name="active" value="1" true-value="1" false-value="0"
                v-model="wordList.active" />
            aktiv
        </label>
    </div>
    <div v-if="subscriptionList != null" class="input-group mt-3">
        <label>
            Mandant
            <select class="form-control" name="subscription_id" v-model="wordList.subscription_id">
                <option value>-</option>
                <option v-for="(subscription, index) in subscriptionList" :value="index">{{ subscription }}</option>
            </select>
        </label>
    </div>
    <button v-if="wordList.relation_type != 4" class="btn btn-primary mt-3" type="button" data-toggle="collapse"
        data-target="#collapse-word-list-more">Erweiterte Optionen</button>

    <div v-if="wordList.relation_type != 4" id="collapse-word-list-more" class="collapse">
        <div class="card card-body mt-3">
            <div class="form-check mb-2">
                <label class="form-check-label">
                    <input type="hidden" name="is_global" value="0" />
                    <input class="form-check-input" type="checkbox" name="is_global" value="1" true-value="1"
                        false-value="0" v-model="wordList.is_global" />
                    Wortliste für alle Mandanten zeigen (Nur wenn kein Mandant gesetzt ist)
                </label>
            </div>
            <div class="form-check mb-2">
                <label class="form-check-label">
                    <input type="hidden" name="use_description" value="0" />
                    <input class="form-check-input" type="checkbox" name="use_description" value="1" true-value="1"
                        false-value="0" v-model="wordList.use_description" />
                    Feld für Erklärung anzeigen
                </label>
            </div>
            <div class="form-check mb-2">
                <label class="form-check-label">
                    <input type="hidden" name="use_for_ct" value="0" />
                    <input class="form-check-input" type="checkbox" name="use_for_ct" value="1" true-value="1"
                        false-value="0" v-model="wordList.use_for_ct" />
                    Brand-Fit Trigger
                </label>
            </div>
            <div class="form-check mb-2">
                <label class="form-check-label">
                    <input type="hidden" name="prioritize_title" value="0" />
                    <input class="form-check-input" type="checkbox" name="prioritize_title" value="1" true-value="1"
                        false-value="0" v-model="wordList.prioritize_title" />
                    Zeige in der App den Titel des Eintrages
                </label>
            </div>
            <div class="form-check mb-2">
                <label class="form-check-label">
                    <input type="hidden" name="can_use_ai_suggestions" value="0" />
                    <input class="form-check-input" type="checkbox" name="can_use_ai_suggestions" value="1"
                        true-value="1" false-value="0" v-model="wordList.can_use_ai_suggestions" />
                    AI-Vorschläge für diese Liste erlauben
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Suchmodus
                    <select class="form-control" name="search_mode" v-model="wordList.search_mode">
                        <option value="0">nur ganze Wörter</option>
                        <option value="1">Wort-Teile erkennen (max. 100 Einträge)</option>
                        <option value="2">erweiterte Suche</option>
                    </select>
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Score Darstellung
                    <select class="form-control" name="scale_type" v-model="wordList.scale_type">
                        <option value="0">keine (Benchmark: absolut)</option>
                        <option value="1">Skala (Benchmark: relativ)</option>
                    </select>
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Benchmark
                    <input class="form-control" type="number" step="0.01" name="scale_benchmark"
                        v-model="wordList.scale_benchmark" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Bezeichnung für Haupteintrag (wird im Backend sichtbar)
                    <input class="form-control" type="text" name="title_root_entry"
                        v-model="wordList.title_root_entry" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Bezeichnung für alternative Einträge (wird in App sichtbar)
                    <input class="form-control" type="text" name="title_entries" v-model="wordList.title_entries" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    String ID (leer für automatisch)
                    <input class="form-control" type="text" name="name" v-model="wordList.name" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Prio (für Sortierung in App)
                    <input class="form-control" type="number" step="1" name="order" v-model="wordList.order" />
                </label>
            </div>
            <div class="input-group">
                <label>
                    Referenz-Liste (ID)
                    <input class="form-control" type="number" step="1" name="ref_word_list_id"
                        v-model="wordList.ref_word_list_id" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Datums-Muster
                    <input class="form-control" type="text" name="date_pattern" v-model="wordList.date_pattern" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Uhrzeit-Muster
                    <input class="form-control" type="text" name="time_pattern" v-model="wordList.time_pattern" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Telefon-Muster
                    <input class="form-control" type="text" name="phone_pattern" v-model="wordList.phone_pattern" />
                </label>
            </div>
            <div class="input-group mb-3">
                <label>
                    Dynamische Gender Schreibweisen
                    <select class="form-control" name="gender_type" v-model="wordList.gender_type">
                        <option value=""></option>
                        <option value="1">mit "*"</option>
                        <option value="2">mit "_"</option>
                        <option value="3">mit ":"</option>
                        <option value="4">mit "/"</option>
                        <option value="5">mit "/-"</option>
                        <option value="6">mit Binnen-I</option>
                        <option value="7">mit Klammern</option>
                        <!-- <option value="8">Doppelschreibweise mit "und"</option>
                        <option value="9">Doppelschreibweise mit "/"</option> -->
                        <option value="0">Alle anzeigen</option>
                    </select>
                </label>
            </div>
        </div>
    </div>
    <ErrorDisplay :errors="errors"></ErrorDisplay>
</template>
